

import { MODEL_PRICING } from '../types';
import { SEEDANCE_2_5_LIMITS, type ReferenceMediaLimits } from '../utils/referenceClips';

export type ModelProvider = 'google' | 'openai' | 'webllm' | 'comfyui' | 'venice';
export type MediaType = 'image' | 'video' | 'text';
export type VideoGenerationMode =
  | 'text-to-video'
  | 'image-to-video'
  | 'frames-to-video'
  | 'ingredients-to-video'
  | 'extend-video'
  | 'reference-to-video';

export interface ModelParameterOption {
  label: string;
  value: string;
  costMultiplier?: number; // Some options (like HD) might increase cost
}

export interface ModelDefinition {
  id: string;
  label: string;
  provider: ModelProvider;
  type: MediaType;
  baseCost: number;
  
  // Supported Configurations
  supportedRatios?: ModelParameterOption[];
  supportedResolutions?: ModelParameterOption[];
  supportedDurations?: ModelParameterOption[];
  supportedQualities?: ModelParameterOption[];
  supportedStyles?: ModelParameterOption[];
  supportedSizes?: ModelParameterOption[]; // Specific to DALL-E/Sora pixels
  
  // Capabilities
  supportsImageReference: boolean;
  maxReferenceImages?: number;
  requiresReferenceImage?: boolean; // e.g., editing models
  supportsTextPrompt: boolean;
  
  // Video generation modes (e.g. text-to-video, image-to-video, frames-to-video, etc.)
  supportedVideoModes?: string[];
  
  // Limits
  maxGeneratedImages?: number;
  
  // API Specifics
  apiModelId?: string; // If different from id
  
  // ComfyUI Specifics
  checkpointPattern?: string; // Regex or string to match checkpoint name
  defaultSteps?: number; // Default steps for this model (e.g. 4 for Schnell)
  
  // OpenAI GPT Image Specifics
  defaultQuality?: 'low' | 'medium' | 'high'; // Quality preset for GPT Image models

  // Venice specifics (values mirror GET https://api.venice.ai/api/v1/models)
  editApiModelId?: string;            // Venice edit/inpaint model used when reference images are connected
  veniceModeModelIds?: Partial<Record<VideoGenerationMode, string>>; // Seedance: one Venice model id per generation mode
  supportsAudioToggle?: boolean;      // Seedance: audio generation can be switched off
  audioAlwaysOn?: boolean;            // Kling 4.0: every clip has sound, and it cannot be switched off
  audioInputModes?: VideoGenerationMode[]; // modes that accept an audio file (audio_url / reference_audio_urls)
  videoInputModes?: VideoGenerationMode[]; // modes that accept reference clips (reference_video_urls)
  supportsExtendHint?: boolean;       // Seedance 2.5: accepts omni_reference_task_type "extend" to continue a clip
  referenceElements?: boolean;        // Kling O3: reference mode sends elements (@Element1…) and scene pictures (@Image1…), not a flat list
  referenceMediaLimits?: ReferenceMediaLimits; // Seedance: how many and how long reference clips and audio may be (2.0 when unset)
  promptCharacterLimit?: number;      // Venice enforces this server-side; we pre-check for a friendly error
  defaultResolution?: string;         // "1K" | "2K" | "4K" for resolution-tier image models
  imagePricing?: {
    byResolution?: Record<string, number>;                 // USD per image, keyed by resolution tier
    byResolutionQuality?: Record<string, Record<string, number>>; // USD per image, [resolution][quality]
  };
  // Pricing of the *-edit model used when reference images are connected (GET /models?type=inpaint)
  editImagePricing?: {
    flat?: number;
    byResolution?: Record<string, number>;
    byResolutionQuality?: Record<string, Record<string, number>>;
    includedInputImages?: number;      // inputs covered by the base price
    additionalInputImageUsd?: number;  // surcharge per extra input image
  };
}

/**
 * Keep a persisted option value only if the given model still offers it;
 * otherwise prefer `preferred`, then the model's first option. Nodes call this
 * so a value saved for one model (e.g. Veo duration "5") is never sent to
 * another (Seedance needs "5s").
 */
export const pickSupportedOption = (
  saved: string | undefined,
  opts: ModelParameterOption[] | undefined,
  preferred: string,
): string => {
  if (!opts || opts.length === 0) return saved || preferred;
  const has = (v?: string) => !!v && opts.some(o => o.value === v);
  if (has(saved)) return saved as string;
  if (has(preferred)) return preferred;
  return opts[0].value;
};

// --- SHARED OPTIONS ---
const RATIOS_GEMINI: ModelParameterOption[] = [
  { label: '1:1 (Square)', value: '1:1' },
  { label: '3:2', value: '3:2' },
  { label: '2:3', value: '2:3' },
  { label: '3:4 (Portrait)', value: '3:4' },
  { label: '4:3 (Landscape)', value: '4:3' },
  { label: '4:5', value: '4:5' },
  { label: '5:4', value: '5:4' },
  { label: '9:16 (Story)', value: '9:16' },
  { label: '16:9 (Widescreen)', value: '16:9' },
  { label: '21:9 (Cinematic)', value: '21:9' },
];

const RATIOS_VEO: ModelParameterOption[] = [
  { label: '16:9 (Landscape)', value: '16:9' },
  { label: '9:16 (Story)', value: '9:16' },
];

const RESOLUTIONS_VIDEO: ModelParameterOption[] = [
  { label: '720p', value: '720p' },
  { label: '1080p', value: '1080p' },
];

const SORA_DURATIONS: ModelParameterOption[] = [
    { label: '4s', value: '4' },
    { label: '8s', value: '8' },
    { label: '12s', value: '12' },
];

const VEO_31_DURATIONS: ModelParameterOption[] = [
    { label: '4s', value: '4' },
    { label: '6s', value: '6' },
    { label: '8s', value: '8' },
];

const VEO_FAST_DURATIONS: ModelParameterOption[] = [
    { label: '5s', value: '5' },
    { label: '6s', value: '6' },
    { label: '8s', value: '8' },
];

const RESOLUTIONS_VEO_31: ModelParameterOption[] = [
    { label: '720p', value: '720p' },
    { label: '1080p (8s only)', value: '1080p' },
    { label: '4K (8s only)', value: '4k' },
];

// OpenAI GPT Image sizes
const SIZES_GPT_IMAGE: ModelParameterOption[] = [
    { label: 'Square (1024x1024)', value: '1024x1024' },
    { label: 'Landscape (1536x1024)', value: '1536x1024' },
    { label: 'Portrait (1024x1536)', value: '1024x1536' },
];

// --- VENICE OPTIONS (from GET /models constraints) ---
const RATIOS_VENICE_IMAGE: ModelParameterOption[] = [
    { label: '1:1 (Square)', value: '1:1' },
    { label: '3:2', value: '3:2' },
    { label: '2:3', value: '2:3' },
    { label: '4:5', value: '4:5' },
    { label: '3:4 (Portrait)', value: '3:4' },
    { label: '16:9 (Widescreen)', value: '16:9' },
    { label: '9:16 (Story)', value: '9:16' },
    { label: '21:9 (Cinematic)', value: '21:9' },
];
const RATIOS_VENICE_GPT_IMAGE_15: ModelParameterOption[] = [
    { label: '1:1 (Square)', value: '1:1' },
    { label: '3:2 (Landscape)', value: '3:2' },
    { label: '2:3 (Portrait)', value: '2:3' },
];
const RESOLUTIONS_VENICE_IMAGE: ModelParameterOption[] = [
    { label: '1K', value: '1K' },
    { label: '2K', value: '2K' },
    { label: '4K', value: '4K' },
];
const QUALITIES_VENICE_GPT_IMAGE: ModelParameterOption[] = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
];

// Seedance (ByteDance via Venice). Aspect ratios apply to text-to-video and
// reference-to-video; image-to-video derives the ratio from the first frame.
const RATIOS_SEEDANCE: ModelParameterOption[] = [
    { label: '16:9 (Widescreen)', value: '16:9' },
    { label: '9:16 (Story)', value: '9:16' },
    { label: '1:1 (Square)', value: '1:1' },
    { label: '4:3', value: '4:3' },
    { label: '3:4 (Portrait)', value: '3:4' },
    { label: '21:9 (Cinematic)', value: '21:9' },
];
const RES_SEEDANCE_HD: ModelParameterOption[] = [
    { label: '720p', value: '720p' },
    { label: '1080p', value: '1080p' },
    { label: '480p', value: '480p' },
];
const RES_SEEDANCE_UHD: ModelParameterOption[] = [
    { label: '720p', value: '720p' },
    { label: '1080p', value: '1080p' },
    { label: '4K', value: '4k' },
    { label: '480p', value: '480p' },
];
const RES_SEEDANCE_LITE: ModelParameterOption[] = [
    { label: '720p', value: '720p' },
    { label: '480p', value: '480p' },
];
const seedanceDurations = (min: number, max: number): ModelParameterOption[] =>
    Array.from({ length: max - min + 1 }, (_, i) => min + i).map(n => ({ label: `${n}s`, value: `${n}s` }));
const SEEDANCE_MODES: VideoGenerationMode[] = ['text-to-video', 'image-to-video', 'reference-to-video'];
const seedanceModeIds = (family: string, withReference: boolean): Partial<Record<VideoGenerationMode, string>> => ({
    'text-to-video': `${family}-text-to-video-basic`,
    'image-to-video': `${family}-image-to-video-basic`,
    ...(withReference ? { 'reference-to-video': `${family}-reference-to-video-basic` } : {}),
});

// Kling (Kuaishou via Venice). Text-to-video and reference mode take an aspect ratio; image-to-video
// follows the first frame. Up to V3 and O3 none has a resolution setting: it is part of the model
// (Standard, Pro, 4K). Kling 4.0 has one (720p, 1080p) and a wider frame (21:9).
const RATIOS_KLING: ModelParameterOption[] = [
    { label: '16:9 (Widescreen)', value: '16:9' },
    { label: '9:16 (Story)', value: '9:16' },
    { label: '1:1 (Square)', value: '1:1' },
];
const RATIOS_KLING_4: ModelParameterOption[] = [...RATIOS_KLING, { label: '21:9 (Cinematic)', value: '21:9' }];
const KLING_SHORT_DURATIONS: ModelParameterOption[] = [
    { label: '5s', value: '5s' },
    { label: '10s', value: '10s' },
];
const klingModeIds = (family: string, modes: VideoGenerationMode[]): Partial<Record<VideoGenerationMode, string>> =>
    Object.fromEntries(modes.map(mode => [mode, `kling-${family}-${mode}`]));
/**
 * One Kling family as a model: the modes Venice offers it in, its frames, resolutions and
 * durations, whether its sound can be switched off (or is always on), and its prompt limit.
 */
interface KlingOptions {
    ratios?: ModelParameterOption[];
    /** Only Kling 4.0 has one; for the others the resolution is part of the model (Standard, Pro, 4K) */
    resolutions?: ModelParameterOption[];
    durations?: ModelParameterOption[];
    /** true: sound that can be switched off; false: no sound; 'always': sound that cannot be switched off */
    audio?: boolean | 'always';
    /** The catalog states none for most families, so Venice's default of 2,500 applies to the prompt and the negative prompt */
    promptLimit?: number;
}
const kling = (
    id: string, label: string, family: string, modes: VideoGenerationMode[],
    { ratios = RATIOS_KLING, resolutions, durations = seedanceDurations(3, 15), audio = true, promptLimit = 2500 }: KlingOptions = {},
): ModelDefinition => ({
    id,
    label,
    provider: 'venice',
    type: 'video',
    baseCost: 0,
    supportedRatios: ratios,
    ...(resolutions ? { supportedResolutions: resolutions } : {}),
    supportedDurations: durations,
    supportsImageReference: modes.some(mode => mode !== 'text-to-video'),
    // Reference mode: up to 4 elements and 4 scene pictures (checked per kind before sending)
    maxReferenceImages: modes.includes('reference-to-video') ? 8 : 1,
    supportsTextPrompt: true,
    supportedVideoModes: modes,
    veniceModeModelIds: klingModeIds(family, modes),
    supportsAudioToggle: audio === true,
    ...(audio === 'always' ? { audioAlwaysOn: true } : {}),
    referenceElements: modes.includes('reference-to-video') || undefined,
    promptCharacterLimit: promptLimit,
});
const KLING_MODES: VideoGenerationMode[] = ['text-to-video', 'image-to-video'];
const KLING_O3_MODES: VideoGenerationMode[] = ['text-to-video', 'image-to-video', 'reference-to-video'];

// --- MODEL REGISTRY ---
export const AI_MODELS: Record<string, ModelDefinition> = {
    // --- VIDEO MODELS ---
    'veo-3.1-fast-generate-preview': {
        id: 'veo-3.1-fast-generate-preview',
        label: 'Veo Fast',
        provider: 'google',
        type: 'video',
        baseCost: 0.20,
        supportedRatios: RATIOS_VEO,
        supportedResolutions: RESOLUTIONS_VIDEO,
        supportedDurations: VEO_FAST_DURATIONS,
        supportsImageReference: true,
        maxReferenceImages: 2, // Start/End frames
        supportsTextPrompt: true,
        supportedVideoModes: ['text-to-video', 'image-to-video', 'frames-to-video'],
    },
    'veo-3.1-generate-preview': {
        id: 'veo-3.1-generate-preview',
        label: 'Veo High Quality',
        provider: 'google',
        type: 'video',
        baseCost: 0.40,
        supportedRatios: RATIOS_VEO,
        supportedResolutions: RESOLUTIONS_VEO_31,
        supportedDurations: VEO_31_DURATIONS,
        supportsImageReference: true,
        maxReferenceImages: 3,
        supportsTextPrompt: true,
        supportedVideoModes: ['text-to-video', 'image-to-video', 'frames-to-video', 'ingredients-to-video', 'extend-video'],
    },
    'sora-2': {
        id: 'sora-2',
        label: 'Sora 2',
        provider: 'openai',
        type: 'video',
        baseCost: 0.25,
        supportedRatios: RATIOS_VEO,
        supportedResolutions: RESOLUTIONS_VIDEO,
        supportedDurations: SORA_DURATIONS,
        supportsImageReference: true,
        maxReferenceImages: 1,
        supportsTextPrompt: true,
    },
    'sora-2-pro': {
        id: 'sora-2-pro',
        label: 'Sora 2 Pro',
        provider: 'openai',
        type: 'video',
        baseCost: 0.50,
        supportedRatios: RATIOS_VEO,
        supportedResolutions: RESOLUTIONS_VIDEO,
        supportedDurations: SORA_DURATIONS,
        supportsImageReference: true,
        maxReferenceImages: 1,
        supportsTextPrompt: true,
    },

    // --- VIDEO MODELS (Venice · Seedance) ---
    // Pricing is variable (duration × resolution × audio); the node asks /video/quote.
    'venice-seedance-2-5': {
        id: 'venice-seedance-2-5',
        label: 'Seedance 2.5 (Venice)',
        provider: 'venice',
        type: 'video',
        baseCost: 0,
        supportedRatios: RATIOS_SEEDANCE,
        supportedResolutions: RES_SEEDANCE_HD,
        supportedDurations: seedanceDurations(4, 30),
        supportsImageReference: true,
        maxReferenceImages: 30,
        supportsTextPrompt: true,
        supportedVideoModes: SEEDANCE_MODES,
        veniceModeModelIds: seedanceModeIds('seedance-2-5', true),
        supportsAudioToggle: true,
        audioInputModes: ['reference-to-video'],
        videoInputModes: ['reference-to-video'],
        supportsExtendHint: true,
        referenceMediaLimits: SEEDANCE_2_5_LIMITS,
        promptCharacterLimit: 15000,
    },
    'venice-seedance-2-0': {
        id: 'venice-seedance-2-0',
        label: 'Seedance 2.0 (Venice)',
        provider: 'venice',
        type: 'video',
        baseCost: 0,
        supportedRatios: RATIOS_SEEDANCE,
        supportedResolutions: RES_SEEDANCE_UHD,
        supportedDurations: seedanceDurations(4, 15),
        supportsImageReference: true,
        maxReferenceImages: 9,
        supportsTextPrompt: true,
        supportedVideoModes: SEEDANCE_MODES,
        veniceModeModelIds: seedanceModeIds('seedance-2-0', true),
        supportsAudioToggle: true,
        audioInputModes: ['reference-to-video'],
        videoInputModes: ['reference-to-video'],
        promptCharacterLimit: 10000,
    },
    'venice-seedance-2-0-fast': {
        id: 'venice-seedance-2-0-fast',
        label: 'Seedance 2.0 Fast (Venice)',
        provider: 'venice',
        type: 'video',
        baseCost: 0,
        supportedRatios: RATIOS_SEEDANCE,
        supportedResolutions: RES_SEEDANCE_LITE,
        supportedDurations: seedanceDurations(4, 15),
        supportsImageReference: true,
        maxReferenceImages: 9,
        supportsTextPrompt: true,
        supportedVideoModes: SEEDANCE_MODES,
        veniceModeModelIds: seedanceModeIds('seedance-2-0-fast', true),
        supportsAudioToggle: true,
        audioInputModes: ['reference-to-video'],
        videoInputModes: ['reference-to-video'],
        promptCharacterLimit: 3500,
    },
    'venice-seedance-2-0-mini': {
        id: 'venice-seedance-2-0-mini',
        label: 'Seedance 2.0 Mini (Venice)',
        provider: 'venice',
        type: 'video',
        baseCost: 0,
        supportedRatios: RATIOS_SEEDANCE,
        supportedResolutions: RES_SEEDANCE_LITE,
        supportedDurations: seedanceDurations(4, 15),
        supportsImageReference: true,
        maxReferenceImages: 9,
        supportsTextPrompt: true,
        supportedVideoModes: SEEDANCE_MODES,
        veniceModeModelIds: seedanceModeIds('seedance-2-0-mini', true),
        supportsAudioToggle: true,
        audioInputModes: ['reference-to-video'],
        videoInputModes: ['reference-to-video'],
        promptCharacterLimit: 10000,
    },
    'venice-seedance-1-5-pro': {
        id: 'venice-seedance-1-5-pro',
        label: 'Seedance 1.5 Pro (Venice)',
        provider: 'venice',
        type: 'video',
        baseCost: 0,
        supportedRatios: RATIOS_SEEDANCE,
        supportedResolutions: RES_SEEDANCE_HD,
        supportedDurations: seedanceDurations(4, 12),
        supportsImageReference: true,
        maxReferenceImages: 1,
        supportsTextPrompt: true,
        supportedVideoModes: ['text-to-video', 'image-to-video'],
        veniceModeModelIds: seedanceModeIds('seedance-1-5-pro', false),
        supportsAudioToggle: true,
        promptCharacterLimit: 3500,
    },

    // --- VIDEO MODELS (Venice · Kling) ---
    // From GET /models?type=video. Priced by duration, resolution and sound; the node asks /video/quote.
    // Left out: the V3 4K reference model (its picture format is not documented) and the motion
    // control models (they copy the motion of a video onto a picture).
    // Kling 4.0 (catalog of 2026-10-30): 720p or 1080p, 16:9, 9:16, 1:1 or 21:9, 3 to 30 s, sound on
    // every clip, prompts of 26,000 characters; about $0.20 a second at 720p and $0.29 at 1080p. Its
    // reference mode is sent as O3's is (elements and scene pictures): Venice documents no other form
    // for it, and the prompt names pictures the same way (@Element1…, @Image1…). The "auto" frame
    // the catalog lists for that mode (follow the pictures) is not offered: text-to-video refuses it.
    'venice-kling-4-0': kling('venice-kling-4-0', 'Kling 4.0 (Venice)', 'v4', KLING_O3_MODES, {
        ratios: RATIOS_KLING_4, resolutions: RESOLUTIONS_VIDEO, durations: seedanceDurations(3, 30), audio: 'always', promptLimit: 26000,
    }),
    'venice-kling-o3-pro': kling('venice-kling-o3-pro', 'Kling O3 Pro (Venice)', 'o3-pro', KLING_O3_MODES),
    'venice-kling-o3-standard': kling('venice-kling-o3-standard', 'Kling O3 Standard (Venice)', 'o3-standard', KLING_O3_MODES),
    'venice-kling-o3-4k': kling('venice-kling-o3-4k', 'Kling O3 4K (Venice)', 'o3-4k', KLING_O3_MODES),
    'venice-kling-v3-pro': kling('venice-kling-v3-pro', 'Kling V3 Pro (Venice)', 'v3-pro', KLING_MODES),
    'venice-kling-v3-standard': kling('venice-kling-v3-standard', 'Kling V3 Standard (Venice)', 'v3-standard', KLING_MODES),
    'venice-kling-v3-4k': kling('venice-kling-v3-4k', 'Kling V3 4K (Venice)', 'v3-4k', ['text-to-video']),
    'venice-kling-v3-turbo-pro': kling('venice-kling-v3-turbo-pro', 'Kling V3 Turbo Pro (Venice)', 'v3-turbo-pro', KLING_MODES, { audio: false }),
    'venice-kling-v3-turbo-standard': kling('venice-kling-v3-turbo-standard', 'Kling V3 Turbo Standard (Venice)', 'v3-turbo-standard', KLING_MODES, { audio: false }),
    'venice-kling-2-6-pro': kling('venice-kling-2-6-pro', 'Kling 2.6 Pro (Venice)', '2.6-pro', KLING_MODES, { durations: KLING_SHORT_DURATIONS }),
    'venice-kling-2-5-turbo-pro': kling('venice-kling-2-5-turbo-pro', 'Kling 2.5 Turbo Pro (Venice)', '2.5-turbo-pro', KLING_MODES, { durations: KLING_SHORT_DURATIONS, audio: false }),

    // --- IMAGE MODELS (Venice) ---
    // Nano Banana Pro (Google) and GPT Image (OpenAI) through Venice. Text-to-image
    // goes to /image/generate; with connected images the *-edit model is used via
    // /image/multi-edit (up to 6 inputs, combined).
    'venice-nano-banana-pro': {
        id: 'venice-nano-banana-pro',
        label: 'Nano Banana Pro (Venice)',
        provider: 'venice',
        type: 'image',
        baseCost: 0.18,
        supportedRatios: RATIOS_VENICE_IMAGE,
        supportedResolutions: RESOLUTIONS_VENICE_IMAGE,
        defaultResolution: '1K',
        supportsImageReference: true,
        maxReferenceImages: 6,
        maxGeneratedImages: 4,
        supportsTextPrompt: true,
        apiModelId: 'nano-banana-pro',
        editApiModelId: 'nano-banana-pro-edit',
        promptCharacterLimit: 32768,
        imagePricing: { byResolution: { '1K': 0.18, '2K': 0.23, '4K': 0.35 } },
        editImagePricing: { byResolution: { '1K': 0.18, '2K': 0.23, '4K': 0.35 } },
    },
    'venice-gpt-image-2': {
        id: 'venice-gpt-image-2',
        label: 'GPT Image 2 (Venice)',
        provider: 'venice',
        type: 'image',
        baseCost: 0.27,
        supportedRatios: RATIOS_VENICE_IMAGE,
        supportedResolutions: RESOLUTIONS_VENICE_IMAGE,
        defaultResolution: '1K',
        supportedQualities: QUALITIES_VENICE_GPT_IMAGE,
        defaultQuality: 'high',
        supportsImageReference: true,
        maxReferenceImages: 6,
        maxGeneratedImages: 4,
        supportsTextPrompt: true,
        apiModelId: 'gpt-image-2',
        editApiModelId: 'gpt-image-2-edit',
        promptCharacterLimit: 10000,
        imagePricing: {
            byResolutionQuality: {
                '1K': { low: 0.02, medium: 0.07, high: 0.26 },
                '2K': { low: 0.03, medium: 0.13, high: 0.50 },
                '4K': { low: 0.05, medium: 0.21, high: 0.83 },
            },
        },
        editImagePricing: {
            byResolutionQuality: {
                '1K': { low: 0.03, medium: 0.08, high: 0.34 },
                '2K': { low: 0.03, medium: 0.14, high: 0.52 },
                '4K': { low: 0.04, medium: 0.22, high: 0.84 },
            },
            includedInputImages: 1,
            additionalInputImageUsd: 0.0092,
        },
    },
    'venice-gpt-image-2-5-flare': {
        id: 'venice-gpt-image-2-5-flare',
        label: 'GPT Image 2.5 Flare (Venice)',
        provider: 'venice',
        type: 'image',
        baseCost: 0.07,
        supportedRatios: RATIOS_VENICE_IMAGE,
        supportedResolutions: RESOLUTIONS_VENICE_IMAGE,
        defaultResolution: '1K',
        supportedQualities: QUALITIES_VENICE_GPT_IMAGE,
        defaultQuality: 'high',
        supportsImageReference: true,
        maxReferenceImages: 6,
        maxGeneratedImages: 4,
        supportsTextPrompt: true,
        apiModelId: 'gpt-image-2-5-flare',
        editApiModelId: 'gpt-image-2-5-flare-edit',
        promptCharacterLimit: 10000,
        imagePricing: {
            byResolutionQuality: {
                '1K': { low: 0.03, medium: 0.04, high: 0.07 },
                '2K': { low: 0.04, medium: 0.05, high: 0.15 },
                '4K': { low: 0.05, medium: 0.07, high: 0.23 },
            },
        },
        editImagePricing: {
            byResolutionQuality: {
                '1K': { low: 0.04, medium: 0.04, high: 0.08 },
                '2K': { low: 0.04, medium: 0.06, high: 0.15 },
                '4K': { low: 0.05, medium: 0.08, high: 0.24 },
            },
            includedInputImages: 1,
            additionalInputImageUsd: 0.00966,
        },
    },
    'venice-gpt-image-2-5-sunburst': {
        id: 'venice-gpt-image-2-5-sunburst',
        label: 'GPT Image 2.5 Sunburst (Venice)',
        provider: 'venice',
        type: 'image',
        baseCost: 0.07,
        supportedRatios: RATIOS_VENICE_IMAGE,
        supportedResolutions: RESOLUTIONS_VENICE_IMAGE,
        defaultResolution: '1K',
        supportedQualities: QUALITIES_VENICE_GPT_IMAGE,
        defaultQuality: 'high',
        supportsImageReference: true,
        maxReferenceImages: 6,
        maxGeneratedImages: 4,
        supportsTextPrompt: true,
        apiModelId: 'gpt-image-2-5-sunburst',
        editApiModelId: 'gpt-image-2-5-sunburst-edit',
        promptCharacterLimit: 10000,
        imagePricing: {
            byResolutionQuality: {
                '1K': { low: 0.03, medium: 0.04, high: 0.07 },
                '2K': { low: 0.04, medium: 0.05, high: 0.15 },
                '4K': { low: 0.05, medium: 0.07, high: 0.23 },
            },
        },
        editImagePricing: {
            byResolutionQuality: {
                '1K': { low: 0.04, medium: 0.04, high: 0.08 },
                '2K': { low: 0.04, medium: 0.06, high: 0.15 },
                '4K': { low: 0.05, medium: 0.08, high: 0.24 },
            },
            includedInputImages: 1,
            additionalInputImageUsd: 0.00966,
        },
    },
    'venice-gpt-image-1-5': {
        id: 'venice-gpt-image-1-5',
        label: 'GPT Image 1.5 (Venice)',
        provider: 'venice',
        type: 'image',
        baseCost: 0.26,
        supportedRatios: RATIOS_VENICE_GPT_IMAGE_15,
        supportsImageReference: true,
        maxReferenceImages: 6,
        maxGeneratedImages: 4,
        supportsTextPrompt: true,
        apiModelId: 'gpt-image-1-5',
        editApiModelId: 'gpt-image-1-5-edit',
        promptCharacterLimit: 5000,
        editImagePricing: { flat: 0.31, includedInputImages: 1, additionalInputImageUsd: 0.02806 },
    },

    // --- IMAGE MODELS ---
    'gemini-2.5-flash-image': {
        id: 'gemini-2.5-flash-image',
        label: 'Gemini 2.5 Flash Image',
        provider: 'google',
        type: 'image',
        baseCost: 0.003,
        supportedRatios: RATIOS_GEMINI,
        supportsImageReference: true,
        maxReferenceImages: 3,
        maxGeneratedImages: 10,
        supportsTextPrompt: true,
    },
    'gemini-3-pro-image-preview': {
        id: 'gemini-3-pro-image-preview',
        label: 'Gemini 3 Pro Image',
        provider: 'google',
        type: 'image',
        baseCost: 0.04,
        supportedRatios: RATIOS_GEMINI,
        supportsImageReference: true,
        maxReferenceImages: 14,
        maxGeneratedImages: 4,
        supportsTextPrompt: true,
    },

    // --- OPENAI GPT IMAGE MODELS ---
    'gpt-image-1': {
        id: 'gpt-image-1',
        label: 'GPT Image 1',
        provider: 'openai',
        type: 'image',
        baseCost: 0.04,
        supportedSizes: SIZES_GPT_IMAGE,
        supportsImageReference: true, // Supports up to 16 images via /v1/images/edits
        maxReferenceImages: 16,
        maxGeneratedImages: 10,
        supportsTextPrompt: true,
        apiModelId: 'gpt-image-1',
        defaultQuality: 'medium',
    },
    'gpt-image-1-hq': {
        id: 'gpt-image-1-hq',
        label: 'GPT Image 1 HQ',
        provider: 'openai',
        type: 'image',
        baseCost: 0.08,
        supportedSizes: SIZES_GPT_IMAGE,
        supportsImageReference: true,
        maxReferenceImages: 16,
        maxGeneratedImages: 10,
        supportsTextPrompt: true,
        apiModelId: 'gpt-image-1',
        defaultQuality: 'high',
    },
    'gpt-image-1.5': {
        id: 'gpt-image-1.5',
        label: 'GPT Image 1.5',
        provider: 'openai',
        type: 'image',
        baseCost: 0.05,
        supportedSizes: SIZES_GPT_IMAGE,
        supportsImageReference: true,
        maxReferenceImages: 16,
        maxGeneratedImages: 10,
        supportsTextPrompt: true,
        apiModelId: 'gpt-image-1.5',
        defaultQuality: 'medium',
    },
    'gpt-image-1.5-hq': {
        id: 'gpt-image-1.5-hq',
        label: 'GPT Image 1.5 HQ',
        provider: 'openai',
        type: 'image',
        baseCost: 0.10,
        supportedSizes: SIZES_GPT_IMAGE,
        supportsImageReference: true,
        maxReferenceImages: 16,
        maxGeneratedImages: 10,
        supportsTextPrompt: true,
        apiModelId: 'gpt-image-1.5',
        defaultQuality: 'high',
    },

    // --- LOCAL IMAGE MODELS (ComfyUI) ---
    'comfyui-flux': {
        id: 'comfyui-flux',
        label: 'FLUX Dev (Local)',
        provider: 'comfyui',
        type: 'image',
        baseCost: 0, // Free - runs locally
        supportedRatios: RATIOS_GEMINI, // All ratios supported
        supportsImageReference: true, // Enable img2img
        maxReferenceImages: 1,
        maxGeneratedImages: 1,
        supportsTextPrompt: true,
        checkpointPattern: 'flux.*dev',
        defaultSteps: 20,
    },
    'comfyui-flux-schnell': {
        id: 'comfyui-flux-schnell',
        label: 'FLUX Schnell (Local)',
        provider: 'comfyui',
        type: 'image',
        baseCost: 0, // Free - runs locally
        supportedRatios: RATIOS_GEMINI, // All ratios supported
        supportsImageReference: true, // Enable img2img
        maxReferenceImages: 1,
        maxGeneratedImages: 1,
        supportsTextPrompt: true,
        checkpointPattern: 'flux.*schnell',
        defaultSteps: 4,
    },
    'comfyui-sdxl': {
        id: 'comfyui-sdxl',
        label: 'SDXL (Local)',
        provider: 'comfyui',
        type: 'image',
        baseCost: 0, // Free - runs locally
        supportedRatios: RATIOS_GEMINI,
        supportsImageReference: true, // Enable img2img
        maxReferenceImages: 1,
        maxGeneratedImages: 1,
        supportsTextPrompt: true,
        checkpointPattern: 'xl',
        defaultSteps: 30,
    },
    'comfyui-sd15': {
        id: 'comfyui-sd15',
        label: 'SD 1.5 (Local)',
        provider: 'comfyui',
        type: 'image',
        baseCost: 0, // Free - runs locally
        supportedRatios: RATIOS_GEMINI,
        supportsImageReference: true, // Enable img2img
        maxReferenceImages: 1,
        maxGeneratedImages: 1,
        supportsTextPrompt: true,
        checkpointPattern: '1-5|v1-5',
        defaultSteps: 30,
    },
    'comfyui-custom-workflow': {
        id: 'comfyui-custom-workflow',
        label: 'Custom Workflow (Local)',
        provider: 'comfyui',
        type: 'image',
        baseCost: 0,
        supportedRatios: RATIOS_GEMINI,
        supportsImageReference: true,
        maxReferenceImages: 99,
        maxGeneratedImages: 1,
        supportsTextPrompt: true,
        defaultSteps: 20,
    },

    // --- TEXT MODELS (Cloud) ---
    'gemini-2.5-flash': {
        id: 'gemini-2.5-flash',
        label: 'Gemini 2.5 Flash',
        provider: 'google',
        type: 'text',
        baseCost: 0.01,
        supportsImageReference: true, // Multimodal
        supportsTextPrompt: true,
    },
    'gemini-3-pro-preview': {
        id: 'gemini-3-pro-preview',
        label: 'Gemini 3 Pro',
        provider: 'google',
        type: 'text',
        baseCost: 0.05,
        supportsImageReference: true, // Multimodal
        supportsTextPrompt: true,
    },

    // --- TEXT MODELS (Local / WebGPU) ---
    'DeepSeek-R1-Distill-Llama-8B-q4f16_1-MLC': {
        id: 'DeepSeek-R1-Distill-Llama-8B-q4f16_1-MLC',
        label: 'DeepSeek R1 (Local)',
        provider: 'webllm',
        type: 'text',
        baseCost: 0,
        supportsImageReference: false,
        supportsTextPrompt: true,
    },
    'Qwen2.5-7B-Instruct-q4f16_1-MLC': {
        id: 'Qwen2.5-7B-Instruct-q4f16_1-MLC',
        label: 'Qwen 2.5 7B (Local)',
        provider: 'webllm',
        type: 'text',
        baseCost: 0,
        supportsImageReference: false,
        supportsTextPrompt: true,
    }
};

export const getVideoModels = () => Object.values(AI_MODELS).filter(m => m.type === 'video');
export const getImageModels = () => Object.values(AI_MODELS).filter(m => m.type === 'image');
export const getTextModels = () => Object.values(AI_MODELS).filter(m => m.type === 'text');

// Helper to calculate cost dynamically
export const calculateCost = (modelId: string, options: any): number => {
    const model = AI_MODELS[modelId];
    if (!model) return 0;

    let cost = model.baseCost;

    // Venice image models are priced per resolution tier (and quality for GPT Image 2).
    // With reference images the *-edit model is billed instead, plus a per-extra-input surcharge.
    const isEdit = !!options?.edit && !!model.editImagePricing;
    const pricing = isEdit ? model.editImagePricing : model.imagePricing;
    if (pricing) {
        const resolution = String(options?.resolution || model.defaultResolution || '1K');
        const quality = String(options?.quality || model.defaultQuality || 'high');
        const byQuality = pricing.byResolutionQuality?.[resolution]?.[quality];
        const byRes = pricing.byResolution?.[resolution];
        const flat = 'flat' in pricing ? pricing.flat : undefined;
        let price = typeof byQuality === 'number' ? byQuality
                  : typeof byRes === 'number' ? byRes
                  : typeof flat === 'number' ? flat
                  : cost;
        if (isEdit && model.editImagePricing?.additionalInputImageUsd) {
            const included = model.editImagePricing.includedInputImages ?? 1;
            const extra = Math.max(0, Number(options?.inputImages || 1) - included);
            price += extra * model.editImagePricing.additionalInputImageUsd;
        }
        return price;
    }

    return cost;
};

/** Resolve the exact Venice model id for a video definition + generation mode. */
export const getVeniceVideoModelId = (model: ModelDefinition, mode: string): string => {
    const byMode = model.veniceModeModelIds;
    if (byMode) {
        const exact = byMode[mode as VideoGenerationMode];
        if (exact) return exact;
        if (byMode['text-to-video']) return byMode['text-to-video'];
    }
    return model.apiModelId || model.id;
};